<template>
    <v-data-table
        :headers="headers"
        :items="listItems"
        :expanded.sync="expandedItems"
        @click:row="expandItemByClick($event)"
        show-expand
        :loading="isLoading"
        hide-default-footer
        :header-props="{ sortIcon: '' }"
        :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
        :no-results-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
        :loading-text="$t('lang_GENERAL_LOADING_DATATABLE')"
        class="noMinWidth"
    >
    <!-- Top Slot -->
        <template v-slot:top>
            <v-row class="pb-3">
                <v-col cols="2">
                    <DropdownFilter 
                        context="ProfitAndLossFilterIncomeGroup"
                        label="lang_LABEL_RELEASES_CAPTION_INCOME_GROUP"
                        :items="incomeGroupItems"
                        :alvqa="alvqa + '-dropdown-filter-income-group'"
                        :defaultValueNull="true"
                    />
                </v-col>
                <v-col 
                    cols="3" 
                    class="pt-8"
                    :alvqa="alvqa + '-income-sum'"
                >
                    {{ $t('lang_GENERIC_CAPTION_SUM') }}:
                    {{ $n(getSumOfDisplayedRows, 'number') }} {{ $store.getters.getSelectedCurrency }}
                </v-col>
            </v-row>
        </template>

    <!-- Headers -->
        <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
            <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
        </template>
    
    <!-- Items -->
        <!-- Expand Icon -->
        <template v-slot:[`item.data-table-expand`]="{ isExpanded }">
            <v-icon 
                v-if="isExpanded"
                small 
                class="ma-0 pa-0"
                color="buttonStandard"
                :alvqa="alvqa + '-list-row-expand-icon-open'"
            >fa-arrow-alt-circle-down</v-icon>
            <v-icon 
                v-else
                small 
                class="ma-0 pa-0"
                color="buttonStandard"
                :alvqa="alvqa + '-list-row-expand-icon-close'"
            >fa-arrow-alt-circle-right</v-icon>
        </template>
        <template v-slot:[`item.incomeGroup`]="{ item }">
            <div :alvqa="alvqa + '-list-row-text-income-group-'+ getIncomeAlvQa(item.incomeGroup)">
                {{ getIncomeGroup(item.incomeGroup) }}
            </div>
        </template>
        <template v-slot:[`item.prognosePercent`]="{ item }">
            <div 
                class="text-right"
                :alvqa="alvqa + '-list-row-text-prognose-percent-'+ getIncomeAlvQa(item.incomeGroup)"
            >
                {{ $alvPlugin.getNumberLocalization(item.prognosePercent, 'number') }}
            </div>
        </template>
        <template v-slot:[`item.difference`]="{ item }">
            <div 
                class="text-right"
                :alvqa="alvqa + '-list-row-text-difference-'+ getIncomeAlvQa(item.incomeGroup)"
            >
                {{ $alvPlugin.getNumberLocalization(item.difference, 'number') }}
            </div>
        </template>
        <template v-slot:[`item.prognose`]="{ item }">
            <div 
                class="text-right"
                :alvqa="alvqa + '-list-row-text-prognose-'+ getIncomeAlvQa(item.incomeGroup)"
            >
                {{ $alvPlugin.getNumberLocalization(item.prognose, 'number') }}
            </div>
        </template>
        <template v-slot:[`item.income`]="{ item }">
            <div 
                class="text-right"
                :alvqa="alvqa + '-list-row-text-result-'+ getIncomeAlvQa(item.incomeGroup)"
            >
                {{ $alvPlugin.getNumberLocalization(item.income, 'number') }}
            </div>
        </template>

        <!-- EXPANDED -->
        <template v-slot:expanded-item="{ item }">
            <td
                colspan="1"
                style="background-color: var(--v-expandPanelBackground-base);"
            ></td>
            <td 
                class="pl-0"
                :colspan="headers.length - 1"
                style="background-color: var(--v-expandPanelBackground-base);"
            >
                <v-container fluid class="pl-0 pr-0">
                    <v-row>
                        <v-col class="pr-0">
                            <v-data-table         
                                :headers="subheaders"
                                hide-default-footer
                                :items="item.rows"
                                :header-props="{ sortIcon: '' }"
                                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                                class="noMinWidth"
                            >
                            <!-- Headers -->
                                <template v-for="(header, i) in subheaders" v-slot:[`header.${header.value}`]>
                                    <span :alvqa="header.alvqa + '-' + getIncomeAlvQa(item.incomeGroup)" :key="i">{{ header.text }}</span>
                                </template>

                            <!-- Items -->
                                <template v-slot:[`item.description`]="{ item, index }">
                                    <div :alvqa="alvqa + '-list-row-' + index + '-text-description'">
                                            {{ item.description }}
                                    </div>
                                </template>
                                <template v-slot:[`item.quantity`]="{ item, index }">
                                    <div 
                                        class="text-right"
                                        :alvqa="alvqa + '-list-row-' + index + '-text-quantity'"
                                    >
                                        {{ $alvPlugin.getNumberLocalization(item.quantity, 'numberInteger') }}
                                    </div>
                                </template>
                                <template v-slot:[`item.income`]="{ item, index }">
                                    <div 
                                        class="text-right"
                                        :alvqa="alvqa + '-list-row-' + index + '-text-income'"
                                    >
                                        {{ $alvPlugin.getNumberLocalization(item.income, 'number') }}
                                    </div>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-container>
            </td>
        </template>
    </v-data-table>
</template>

<script>
import { DropdownLabelReleasesBudgetGroupTypes } from '@/assets/dropdowns/alvDropdowns.js'

import DropdownFilter from '@/components/_filters/DropdownFilter'

export default {
    name: "ProfitAndLossIncomeDataTableReadOnly",
    components: {
        DropdownFilter
    },
    props: {
        releaseAlvId: {
            type: String
        }
    },
    data() {
        return {
            alvqa: 'label-releases-list-row-' + this.releaseAlvId + '-read-only-panel-profit-and-loss-income',
            expandedItems: []
        }
    },
    computed: {
        headers() {
            return [
                {
					text: '', 
					value: 'data-table-expand',
					align: 'center', 
                    sortable: false,
					width: "48px"
				},
                {
                    text: this.$t('lang_GENERIC_CAPTION_INCOME'), 
                    align: "left", 
                    value: "incomeGroup",
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-income',
                    sortable: false,
                    filter: this.filterIncomeGroup,
                },
                {
                    text: this.$t('lang_LABEL_RELEASES_CAPTION_FORECAST') + " %", 
                    align: "right", 
                    value: "prognosePercent",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-forecast-percent'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_DIFF') + ' ' + this.$store.getters.getSelectedCurrency,
                    align: "right", 
                    value: "difference",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-difference'
                },
                {
                    text: this.$t('lang_LABEL_RELEASES_CAPTION_FORECAST') + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "prognose",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-forecast'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_ACTUAL') + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "income",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-result'
                }
            ]
        },
        subheaders() {
            return [
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_DESCRIPTION"), 
                    align: "left", 
                    value: "description",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-expand-header-description'
                },
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_QUANTITY"), 
                    align: "right", 
                    value: "quantity",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-expand-header-quantity'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_ACTUAL') + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "income",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-expand-header-income'
                }
            ]
        },

        listItems() { return this.$store.getters.getReleasesProfitAndLoss.income },
        isLoading() { return this.$store.getters.getProfitAndLossIsLoading },
        incomeGroupItems() {
            let items = []
            let incomeGroup = JSON.parse(JSON.stringify(DropdownLabelReleasesBudgetGroupTypes))
            if(incomeGroup !== null && incomeGroup !== undefined){
                incomeGroup.forEach(element => {
                    if(element.group === 'income') {
                        element.text = this.$t(element.text)
                        items.push(element)
                    }
                })
            }
            items.sort((a,b) => (a.text > b.text) ? 1 : (b.text > a.text) ? -1 : 0)
            return items
        },
        getSumOfDisplayedRows() {
            let selected = this.$store.getters.getSelectedProfitAndLossFilterIncomeGroup
            let sum = 0

            // If api call for the panel is still pending we return default value 0
            if(this.listItems === undefined) return sum
            // if no filter is set, just sum up the "IST-amount" of all rows.
            if(this.listItems !== undefined && this.listItems !== null){
                if(!selected) sum = this.listItems.reduce((prev, cur) => prev + this.$alvPlugin.stringToNumber(cur.income), 0)
                else { // if the filter is set
    
                    this.listItems.forEach(row => {
                        if(row.incomeGroup === selected) sum += this.$alvPlugin.stringToNumber(row.income)
                    })
                }
            }
            
            return sum
        }
    },
    methods: {
        getIncomeGroup(incomeGroup) {
            let incomeGroupTypes = DropdownLabelReleasesBudgetGroupTypes

            if(incomeGroupTypes !== undefined) {
                let match = incomeGroupTypes.find(type => {
                    return type.value === incomeGroup
                })
                if(match !== undefined){
                    return this.$t(match.text)
                }
            } else {
                return ''
            }
        },
        getIncomeAlvQa(incomeGroupValue) {
            switch(incomeGroupValue) {
                case 'incomeOther': 
                    return 'other'
                    break
                case 'incomeLive':
                    return 'live'
                    break
                default:
                    return incomeGroupValue
                    break
            }
        },
        // Expand behaviour when clicking on a row. 
        async expandItemByClick(item) {            
            if(this.expandedItems.includes(item)) { // close item.
                let indexOfItem = this.expandedItems.indexOf(item)
                this.expandedItems.splice(indexOfItem, 1) 
            } else { // open item             
                this.expandedItems.push(item) 
            }
        },
        filterIncomeGroup(value) {
            // https://www.codeply.com/p/krbUh6q9yE
            let selected = this.$store.getters.getSelectedProfitAndLossFilterIncomeGroup

            if (!selected) return true // return "true" for all rows in data table = show all rows (no filter selected)
            else return value === selected // check if the value of the row is matching the filter and show row in data table
        },
    }
}
</script>

<style>
    .noMinWidth table {
        min-width: 0px !important;
        table-layout: fixed;
    }
</style>